import {DictionaryModel} from "./dictionary.model";
import {makeAutoObservable} from "mobx";
import AppStore from "../../app.store";
import axios from "axios";
import {api} from "../../api/endpoints";
import {toast} from "react-toastify";
import {isConnectivityError} from "../offline/offline.cache";
import {cacheTransactionsLookup, getCachedTransactionsLookupAsync} from "../../pages/transactions/transactions.offline";

import {getRequestErrorReason} from '../requestError';
import {createRequestGuard, PAGE_LIST_LOADING_INITIAL} from '../requestGuard';

const dictionaryListInitialState: DictionaryModel[] = [];
const sidebarDictionaryInitialState: DictionaryModel[] = [];
const loadingInitialState: boolean = PAGE_LIST_LOADING_INITIAL;

class DictionaryStore {
    public dictionaryList = dictionaryListInitialState;
    public sidebarList = sidebarDictionaryInitialState;
    public isLoading = loadingInitialState;
    public pageTitleList = dictionaryListInitialState;
    public modalDictionaryList = dictionaryListInitialState;
    error: string = '';
    message: string = '';
    private loadGuard = createRequestGuard();

    constructor() {
        makeAutoObservable(this);
    }

    private normalizeDictionaryPayload<T>(data: T): T {
        // Backend-driven dictionaries may contain typos; normalize them defensively for UI.
        if (!Array.isArray(data)) return data;

        const applyTypoFixes = (s: string) => {
            let v = s;
            const pairs: [string, string][] = [
                ['Дата закрытыя', 'Дата закрытия'],
                ['Дата концы', 'Дата конца'],
                ['Дата началы', 'Дата начало'],
                ['Все компании', 'Все кампании'],
                ['Номер компании', 'Номер кампании'],
                ['Детали компании', 'Детали кампании'],
                ['скдики', 'скидки'],
                ['скдика', 'скидка'],
                ['скдике', 'скидке'],
                ['скдику', 'скидку'],
                ['Актинвые', 'Активные'],
                ['Актинвый', 'Активный'],
                ['Актинвую', 'Активную'],
            ];
            for (const [wrong, right] of pairs) {
                v = v.replaceAll(wrong, right);
            }
            v = v.replaceAll('Коммисс', 'Комисс');
            v = v.replaceAll('коммисс', 'комисс');
            return v;
        };

        return data.map((item: any) => {
            const value = typeof item?.value === 'string' ? applyTypoFixes(item.value) : item?.value;

            return value === item?.value ? item : {...item, value};
        }) as T;
    }

    setDictionaryList(data: any) {
        this.dictionaryList = this.normalizeDictionaryPayload(data);
    }

    setModalDictionaryList(data: any) {
        this.modalDictionaryList = this.normalizeDictionaryPayload(data);
    }

    setPageTitleList(data: any) {
        this.pageTitleList = this.normalizeDictionaryPayload(data);
    }

    setSidebarDictionaryList(data: any) {
        this.sidebarList = this.normalizeDictionaryPayload(data);
    }

    setLoading(loading: boolean) {
        this.isLoading = loading;
    }

    public destroyDictionary = () => {
        this.dictionaryList = dictionaryListInitialState;
        this.setLoading(true);
    }

    public destroyModalDictionary = () => {
        this.modalDictionaryList = dictionaryListInitialState;
    }

    public dictionaryListReq = async (entityCode: string, propertyCode: string, signal?: AbortSignal) => {
        const requestId = this.loadGuard.next();
        this.setLoading(true);

        const {getToken} = AppStore;
        const headers = {
            Authorization: 'Bearer ' + getToken()!,
        }

        try {

            const response = await axios.get(api.dictionaries + `?entity=${entityCode}&property=${propertyCode}`, {
                headers,
                signal
            });

            void cacheTransactionsLookup('dictionary', {
                entity: entityCode,
                property: propertyCode,
            }, response.data);

            switch (propertyCode) {
                case 'MODAL':
                    this.setModalDictionaryList(response?.data);
                    break;
                default:
                    this.setDictionaryList(response?.data);
                    break;
            }

            return response;
        } catch (e: any) {
            if (isConnectivityError(e)) {
                const cached = await getCachedTransactionsLookupAsync<any>('dictionary', {
                    entity: entityCode,
                    property: propertyCode,
                });

                if (cached) {
                    switch (propertyCode) {
                        case 'MODAL':
                            this.setModalDictionaryList(cached);
                            break;
                        default:
                            this.setDictionaryList(cached);
                            break;
                    }

                    return {
                        data: cached,
                        offlineCached: true,
                    } as any;
                }
            }

            const reason = getRequestErrorReason(e);

            if (!reason) return;

            this.error = reason;
            toast.error(this.error);
        } finally {
            if (this.loadGuard.isLatest(requestId)) {
                this.setLoading(false);
            }
        }
    }

    public sidebarDictionaryReq = async (signal?: AbortSignal) => {
        const {getToken} = AppStore;
        const headers = {
            Authorization: 'Bearer ' + getToken()!,
        }

        try {
            const response = await axios.get(api.dictionaries + `/sidebar`, {headers, signal});

            this.setSidebarDictionaryList(response?.data);
        } catch (e: any) {
            const reason = getRequestErrorReason(e);
            if (!reason) return;
            this.error = reason;
            toast.error(this.error);
        } finally {
        }
    }

    public pageTitleDictionaryReq = async (signal?: AbortSignal) => {
        const {getToken} = AppStore;
        const headers = {
            Authorization: 'Bearer ' + getToken()!,
        }

        try {
            const response = await axios.get(api.dictionaries + `/title`, {headers, signal});

            this.setPageTitleList(response?.data);
        } catch (e: any) {
            const reason = getRequestErrorReason(e);
            if (!reason) return;
            this.error = reason;
            toast.error(this.error);
        } finally {
        }
    }

    public getSidebarLabel = (code: string) => {
        let value = '';
        this.sidebarList?.forEach((item) => {
            if (item.code === code) {
                value = item.value
            }
        })
        return value
    }

    public getTableHeaders = (key: string) => {
        let value = '';
        this.dictionaryList?.forEach((item) => {
            if (item.key === key) {
                value = item.value
            }
        })
        return value
    }

    public getModalPlaceholders = (key: string) => {
        let value = '';
        this.modalDictionaryList?.forEach((item) => {
            if (item.key === key) {
                value = item.value
            }
        })
        return value
    }

    public getPageTitle = (key: string) => {
        let value = '';
        this.pageTitleList?.forEach((item) => {
            if (item.code === key) {
                value = item.value
            }
        })
        return value
    }
}

export default new DictionaryStore();
